import React from 'react';
import { useRef, useMemo } from 'react';
import { Box } from '@mui/material';

import {
  SectionContent,
  StyledList,
  GoBackButton,
  SplitSection,
  ImageTextOverlay
} from '../../components';
import { CSR_CONTENT } from '../../data';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import csrImage02 from '../../assets/images/who-we-are/csr-image-02.jpeg';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const CSRPage = ({ setRefs }) => {
  const csrTitleRef = useRef(null);
  const csrIntroRef = useRef(null);
  const givingRef = useRef(null);
  const founderGadicRef = useRef(null);
  const founderGadicDetailsRef = useRef(null);
  const taxRef = useRef(null);
  const dataCentresRef = useRef(null);
  const otherResponsibilitiesRef = useRef(null);

  const refs = useMemo(
    () => ({
      csrTitle: csrTitleRef,
      csrIntro: csrIntroRef,
      giving: givingRef,
      founderGadic: founderGadicRef,
      founderGadicDetails: founderGadicDetailsRef,
      tax: taxRef,
      dataCentres: dataCentresRef,
      otherResponsibilities: otherResponsibilitiesRef
    }),
    []
  );

  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  if (!CSR_CONTENT || !CSR_CONTENT.SECTIONS) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <SEO
        title={seoData.csr.title}
        content={seoData.csr.content}
        keywords={seoData.csr.keywords}
        canonical={seoData.csr.canonical}
        ogImage={seoData.csr.ogImage}
      />
      <GoBackButton backTo={ROUTES.ABOUT_US.ROOT} backText='Back to About Us' />

      <SectionContent
        id='csrTitle'
        ref={refs.csrTitle}
        title={CSR_CONTENT.TITLE}
        titleVariant='TitleMedium'
        titleMargin='0 0 50px 0'
        initialSpacing={false}
      />

      <SectionContent
        id='csrIntro'
        ref={refs.csrIntro}
        initialSpacing={false}
        text={[
          {
            text: CSR_CONTENT.SECTIONS.INTRO.TEXT,
            textAlign: 'center',
            padding: '2rem 2.5rem',
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            color: THEME.COLORS.NEUTRAL_WHITE,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <SectionContent
        id='giving'
        ref={refs.giving}
        title={CSR_CONTENT.SECTIONS.GIVING.TITLE}
        titleTag='h3'
        titleVariant='TitleSmall'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: CSR_CONTENT.SECTIONS.GIVING.TEXT,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <SectionContent
        id='founderGadic'
        ref={refs.founderGadic}
        title={CSR_CONTENT.SECTIONS.FOUNDER_GADIC.TITLE}
        titleVariant='TitleSmall'
        titleTag='h3'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={CSR_CONTENT.SECTIONS.FOUNDER_GADIC.LINKS}
        text={[
          {
            text: CSR_CONTENT.SECTIONS.FOUNDER_GADIC.TEXT[0],
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      <ImageTextOverlay
        imageSrc={csrImage02}
        text={CSR_CONTENT.SECTIONS.FOUNDER_GADIC.TEXT[1]}
        height={{ xs: '270px', sm: '300px', md: '360px', lg: '400px' }}
        boxSx={{
          width: '100%',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          textAlign: 'center'
        }}
        textSx={{
          fontSize: { xs: '1.07rem', sm: '1.15rem', md: '1.3rem' },
          lineHeight: 1.6,
          color: THEME.COLORS.NEUTRAL_WHITE,
          px: { xs: 2, sm: 3, md: 5 },
          zIndex: 2,
          width: '100%',
          maxWidth: { xs: '100%', md: '80%' }
        }}
        containerSx={{
          position: 'relative',
          width: '100%'
        }}
        overlayColor='rgba(0,0,0,0.5)'
      />
      <SectionContent
        id='founderGadicDetails'
        ref={refs.founderGadicDetails}
        initialSpacing={false}
        linkMappings={CSR_CONTENT.SECTIONS.FOUNDER_GADIC.LINKS}
        text={[
          {
            text: CSR_CONTENT.SECTIONS.FOUNDER_GADIC.TEXT[2],
            marginTop: ELEMENT_SPACING,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem',
            textAlign: 'left'
          }
        ]}
      />

      <SplitSection
        leftContent={
          <div>
            <SectionContent
              id='tax'
              ref={refs.tax}
              title={CSR_CONTENT.SECTIONS.TAX.TITLE}
              titleVariant='TitleSmall'
              titleTag='h3'
              titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
              initialSpacing={false}
              linkMappings={CSR_CONTENT.SECTIONS.TAX.LINKS}
              text={[
                {
                  text: CSR_CONTENT.SECTIONS.TAX.TEXT,
                  padding: { xs: 3, md: 4 },
                  backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.NEUTRAL_WHITE,
                  fontSize: '1.3rem',
                  borderRadius: '4px'
                }
              ]}
            />
          </div>
        }
        rightContent={
          <div>
            <SectionContent
              id='dataCentres'
              ref={refs.dataCentres}
              title={CSR_CONTENT.SECTIONS.DATA_CENTRES.TITLE}
              titleVariant='TitleSmall'
              titleTag='h3'
              titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
              initialSpacing={false}
              text={[
                {
                  text: CSR_CONTENT.SECTIONS.DATA_CENTRES.TEXT,
                  padding: '4rem 2rem',
                  backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.NEUTRAL_WHITE,
                  fontSize: '1.3rem',
                  borderRadius: '4px'
                }
              ]}
            />
          </div>
        }
        sx={{ mt: ELEMENT_SPACING, mb: SECTION_SPACING }}
      />

      <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
        <SectionContent
          id='otherResponsibilities'
          ref={refs.otherResponsibilities}
          title={CSR_CONTENT.SECTIONS.OTHER_RESPONSIBILITIES.TITLE}
          titleVariant='TitleSmall'
          titleTag='h3'
          titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
          initialSpacing={false}
        />

        <StyledList
          items={CSR_CONTENT.SECTIONS.OTHER_RESPONSIBILITIES.LIST_ITEMS}
          titleTag='h4'
          sx={{ mb: ELEMENT_SPACING }}
        />
      </Box>

      <SectionContent
        initialSpacing={false}
        text={[
          {
            text: CSR_CONTENT.SECTIONS.FOOTER_NOTE,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            marginTop: ELEMENT_SPACING,
            marginBottom: ELEMENT_SPACING,
            padding: 0,
            backgroundColor: 'transparent'
          }
        ]}
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      />
    </>
  );
};

export default CSRPage;
